import React, { Component, Dispatch, createRef } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import {
    AbstractSofaForm,
    SofaButton,
    SofaFormComponent,
    SofaFormControl,
    SofaFormControlDescriptionWithGtm,
    SofaFormFilter,
    SofaFormValidator,
    SofaFormWithGtm,
    SofaInput,
    SofaLink,
    generateDispatchToPropsMap,
    getPropsForSofaInput
} from '@sofology/sofologycomponents';

import './AddressEditor.scss';

import { analyticsHelper, errorToBasketErrorModel, scrollHelper } from 'helpers';
import { DataAccessObjectType, getControlFilters, getFormCrossValidationHandler, getIfcPropertyForControl, getStringControlValidators } from 'helpers/dynamicFormHelpers';
import {
    AddressModel,
    AddressSearchEditorModel,
    IfcAddressDescription,
    SofaFormCrossValidationHandlerType,
    createEmptyAddress
} from 'models';
import { AddressView } from './AddressView/AddressView';
import { correctErrorMessages } from './correctErrorMessage';
import { BasketErrorModel } from 'models/Errors';
import { AppState } from 'store';
import AddressSearch, { AddressSearchType } from './AddressSearch/AddressSearch';
import {
    FindAddressFunc,
    InitAddressSearchEditorFunc,
    RemoveAddressSearchEditorFunc,
    findAddress,
    initAddressSearchEditor,
    removeAddressSearchEditor
} from 'store/checkout/actions/addressSearchEditor';

type FormControlsDescriptionType = Record<keyof AddressFormControls, SofaFormControlDescriptionWithGtm<any>>;

export enum AddressEditorModes {
    NotInitialized = 0,
    View,
    ManualEdit,
    Search,
    ReadOnly
}

export interface AddressFormControls extends AbstractSofaForm {
    flat: SofaFormControl<string>;
    buildingName: SofaFormControl<string>;
    houseNumber: SofaFormControl<string>;
    street: SofaFormControl<string>;
    town: SofaFormControl<string>;
    county: SofaFormControl<string>;
    postcode: SofaFormControl<string>;
}

interface OwnProps {
    ref?: React.RefObject<AddressEditorType>;
    ifcAddressQuestionSet: IfcAddressDescription;
    defaultAddress: AddressModel | null;
    gtmFormName: string;
    readonlyAddress?: AddressModel;
    fixedPostcode?: string;
    addressFormId: string;
    additionalValidators?: Partial<Record<keyof AddressFormControls, SofaFormValidator<any>[] | undefined>>;
    startControlIndex: number;
    onChange?: (currentMode: AddressEditorModes, address: AddressModel) => void;
}

interface OwnState {
    form: SofaFormWithGtm<AddressFormControls>;
    currentMode: AddressEditorModes;
    proccessFormCrossValidation: SofaFormCrossValidationHandlerType;
}

interface StateProps {
    addressSearchEditorData: AddressSearchEditorModel | null;
}

interface DispatchProps {
    initAddressSearchEditor: InitAddressSearchEditorFunc;
    removeAddressSearchEditor: RemoveAddressSearchEditorFunc;
    findAddress: FindAddressFunc;
}

class AddressEditor extends Component<OwnProps & StateProps & DispatchProps, OwnState> implements SofaFormComponent<AddressModel> {
    private addressSearchRef = React.createRef<AddressSearchType>();
    private addressElement = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        const formControlsDescriptions = this.createFormControlDescriptions();
        const formName = 'form';
        this.state = {
            currentMode: AddressEditorModes.NotInitialized,
            form: new SofaFormWithGtm<AddressFormControls>(
                formControlsDescriptions,
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                formName,
                () => this.state,
                this.props.gtmFormName,
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel,
                this.props.startControlIndex
            ),
            proccessFormCrossValidation: this.createFormCrossValidationHandler(formControlsDescriptions, formName)
        };
    }

    componentDidMount(): void {
        const isReadOnly = Boolean(this.props.readonlyAddress);

        if (isReadOnly) {
            this.setState({ currentMode: AddressEditorModes.ReadOnly });
            this.initializeAddress(this.props.readonlyAddress!);
        } else if (this.props.defaultAddress) {
            this.setState({ currentMode: AddressEditorModes.View });
            this.handleAddressFound(this.props.defaultAddress);
        } else {
            this.handleSearchByNewPostcode();
        }
    }

    componentDidUpdate(prevProps: OwnProps & StateProps, prevState: Readonly<OwnState>) {
        this.state.proccessFormCrossValidation(prevState, this.state);

        if (!this.props.readonlyAddress && this.props.onChange) {
            this.props.onChange(this.state.currentMode, this.getFormData());
        }
    }

    handleAddressFound = (address: AddressModel) => {
        this.initializeAddress(address);
        // fix issue with not actual state immediately after initialization
        this.setState({ currentMode: AddressEditorModes.NotInitialized });
        setTimeout(() => {
            this.state.form.validate().then(invalidControls => {
                if (invalidControls.length > 0) {
                    this.openManualMode();
                } else {
                    this.setState({ currentMode: AddressEditorModes.View });
                }
            }).catch();
        }, 10);
    };

    handleOpenManualMode = () => {
        this.initializeAddress(createEmptyAddress());
        this.openManualMode();
    };

    openManualMode(): void {
        this.setState({ currentMode: AddressEditorModes.ManualEdit });
    }

    handleSearchByNewPostcode = () => {
        this.props.removeAddressSearchEditor(this.props.addressFormId);
        this.handleOpenSearchMode();
        scrollHelper.scrollTo(this.addressElement, '.validation-scroll-container');
    };

    handleOpenSearchMode = () => {
        this.initializeAddress(createEmptyAddress());
        this.setState({ currentMode: AddressEditorModes.Search });
    };

    handleClearButton = () => {
        this.initializeAddress(createEmptyAddress());
    };

    render(): JSX.Element | null {
        const { currentMode } = this.state;
        const isReadOnly = Boolean(this.props.readonlyAddress);
        const postcodeCanBeChanged = !isReadOnly && !this.props.fixedPostcode;

        return (
            <div className='address-editor' ref={this.addressElement}>
                {this.renderAddressContent()}
                <div>
                    {!isReadOnly && currentMode == AddressEditorModes.Search && <SofaLink onClick={this.handleOpenManualMode} text='Enter address manually' />}
                    {postcodeCanBeChanged && currentMode == AddressEditorModes.ManualEdit && <SofaLink onClick={this.handleSearchByNewPostcode} text='Search by a different postcode' />}
                </div>
            </div>
        );
    }

    renderAddressContent(): JSX.Element {
        switch (this.state.currentMode) {
            case AddressEditorModes.NotInitialized:
                return <div className='address-editor__not-initialized'></div>;
            case AddressEditorModes.ReadOnly:
                return this.renderReadOnlyMode();
            case AddressEditorModes.View:
                return this.renderViewMode();
            case AddressEditorModes.ManualEdit:
                return this.renderManualMode();
            case AddressEditorModes.Search:
                return this.renderSearchMode();
        }
    }

    renderViewMode(): JSX.Element {
        return <AddressView
            address={this.getFormData()}
            onEditClick={this.handleOpenSearchMode}
        />;
    }

    renderReadOnlyMode(): JSX.Element {
        return <AddressView
            address={this.props.readonlyAddress!}
            readonly={true}
        />;
    }

    renderManualMode(): JSX.Element {
        const isSearchResultExists = Boolean(this.props.addressSearchEditorData?.foundAddresses?.length);

        return (
            <div className='address-editor__manual'>
                {isSearchResultExists && <SofaLink onClick={this.handleOpenSearchMode} text='Select your address from drop down' />}
                <Grid container className='address-editor__manual-result'>
                    <Grid item xs={9} sm={10}>
                        <AddressView
                            address={this.getFormData()}
                            readonly={true}
                            onEditClick={() => { }}
                        />
                    </Grid>
                    <Grid item xs={3} sm={2}>
                        <div className='address-editor__clear-button'>
                            <SofaButton
                                text='Clear'
                                variant='small'
                                onClick={this.handleClearButton}
                            />
                        </div>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    {this.renderInput(this.state.form.controls.flat, 'Flat')}
                    {this.renderInput(this.state.form.controls.buildingName, 'Building name')}
                    {this.renderInput(this.state.form.controls.houseNumber, 'House number')}
                    {this.renderInput(this.state.form.controls.street, 'Street')}
                    {this.renderInput(this.state.form.controls.town, 'Town or City')}
                    {this.renderInput(this.state.form.controls.county, 'County')}
                    {this.renderInput(this.state.form.controls.postcode, 'Postcode')}
                </Grid>
            </div>
        );
    }

    renderSearchMode(): JSX.Element {
        return (
            <div className='address-editor__search'>
                <AddressSearch
                    startControlIndex={0}
                    ref={this.addressSearchRef}
                    editorId={this.props.addressFormId}
                    gtmFormName={this.props.gtmFormName}
                    onChange={this.handleAddressFound}
                    fixedPostcode={this.props.fixedPostcode ?? null}
                    addressSearchEditorData={this.props.addressSearchEditorData}
                    initAddressSearchEditor={this.props.initAddressSearchEditor}
                    findAddress={this.props.findAddress}
                    additionalValidators={this.props.additionalValidators}
                    additionalFilters={{ postcode: getControlFilters(this.props.ifcAddressQuestionSet.properties.postCode) as SofaFormFilter<string>[] }}
                />
            </div>
        );
    }

    renderInput(control: SofaFormControl<string>, label: string): JSX.Element {
        label = getIfcPropertyForControl(this.props.ifcAddressQuestionSet.properties, control.name).required ? (label + ' *') : label;

        return (
            <Grid item xs={12}>
                <SofaInput
                    label={label}
                    inputProps={{ autoCorrect: 'off', autoComplete: control.name }}
                    {...getPropsForSofaInput(control)}
                    helperText={correctErrorMessages(control.errorMessage)}
                />
            </Grid>
        );
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        const { currentMode } = this.state;

        if (currentMode === AddressEditorModes.Search) {
            return this.addressSearchRef.current!.validate();
        }

        return this.state.form.validate();

    };

    public getFormData = (): AddressModel => {
        return new AddressModel(
            this.state.form.controls.flat.value,
            this.state.form.controls.buildingName.value,
            this.state.form.controls.houseNumber.value,
            this.state.form.controls.street.value,
            this.state.form.controls.town.value,
            null,
            this.state.form.controls.county.value,
            this.state.form.controls.postcode.value
        );
    };

    initializeAddress(address: AddressModel): void {
        this.state.form.controls.flat.setValue(address.flat || '');
        this.state.form.controls.buildingName.setValue(address.building || '');
        this.state.form.controls.houseNumber.setValue(address.number || '');
        this.state.form.controls.street.setValue(address.street || '');
        this.state.form.controls.town.setValue(address.town || '');
        this.state.form.controls.county.setValue(address.county || '');
        this.state.form.controls.postcode.setValue(address.postcode?.code || '');
    }

    createFormControlDescriptions(): FormControlsDescriptionType {
        const defaultControlsDescriptions = {
            flat: {
                name: 'flat',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'flat'
            } as SofaFormControlDescriptionWithGtm<string>,
            buildingName: {
                name: 'buildingName',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'houseName'
            } as SofaFormControlDescriptionWithGtm<string>,
            houseNumber: {
                name: 'houseNumber',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'houseNumber'
            } as SofaFormControlDescriptionWithGtm<string>,
            street: {
                name: 'street',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'street'
            } as SofaFormControlDescriptionWithGtm<string>,
            town: {
                name: 'town',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'town'
            } as SofaFormControlDescriptionWithGtm<string>,
            county: {
                name: 'county',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'county'
            } as SofaFormControlDescriptionWithGtm<string>,
            postcode: {
                name: 'postcode',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'postcode'
            } as SofaFormControlDescriptionWithGtm<string>,
        };

        const dataAccessObject = {};
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.flat, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.flat, this.props.additionalValidators?.flat);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.buildingName, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.buildingName, this.props.additionalValidators?.buildingName);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.houseNumber, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.houseNumber, this.props.additionalValidators?.houseNumber);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.street, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.street, this.props.additionalValidators?.street);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.town, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.town, this.props.additionalValidators?.town);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.county, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.county, this.props.additionalValidators?.county);
        this.fillControlFiltersAndValidatorsFromIfcQuestionSet(defaultControlsDescriptions.postcode, dataAccessObject);
        this.addAdditionalValidators(defaultControlsDescriptions.postcode, this.props.additionalValidators?.postcode);

        return defaultControlsDescriptions;
    }

    fillControlFiltersAndValidatorsFromIfcQuestionSet(controlDescription: SofaFormControlDescriptionWithGtm<string>, dataAccessObject: DataAccessObjectType): void {
        const propertyDescription = getIfcPropertyForControl(this.props.ifcAddressQuestionSet.properties, controlDescription.name);

        dataAccessObject[propertyDescription.name] = () => this.state.form.controls[controlDescription.name].value;

        controlDescription.filters = getControlFilters(propertyDescription) as SofaFormFilter<string>[];
        controlDescription.validators = getStringControlValidators(propertyDescription, dataAccessObject);
        (controlDescription as any).originalPropertyName = propertyDescription.name;
    }

    addAdditionalValidators(controlDescription: SofaFormControlDescriptionWithGtm<string>, additionalValidators: SofaFormValidator<any>[] | undefined): void {
        if (additionalValidators) {
            controlDescription.validators!.push(...additionalValidators);
        }
    }

    getControlNameByProperty(formControlsDescriptions: FormControlsDescriptionType, propertyName: string): string {
        for (let controlName in formControlsDescriptions) {
            if (formControlsDescriptions[controlName].originalPropertyName === propertyName) {
                return formControlsDescriptions[controlName].name;
            }
        }

        throw new BasketErrorModel(`Form control was not found for property name ${propertyName}`);
    }

    createFormCrossValidationHandler(formControlsDescriptions: FormControlsDescriptionType, formName: string): SofaFormCrossValidationHandlerType {
        return getFormCrossValidationHandler(
            (controlName: string) => getIfcPropertyForControl(this.props.ifcAddressQuestionSet.properties, controlName),
            (propertyName: string) => this.getControlNameByProperty(formControlsDescriptions, propertyName),
            formControlsDescriptions,
            formName);
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [initAddressSearchEditor, removeAddressSearchEditor, findAddress]);
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        addressSearchEditorData: state.checkoutState.addressSearchEditors[ownProps.addressFormId] ?? null
    };
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(AddressEditor);
export type AddressEditorType = AddressEditor;